import ComponentId from "src/constants/componentId.enum";
import Granularity from "src/constants/granularity.enum";
import IntervalType from "src/constants/intervalType.enum";
import { CountItemsDTO } from "src/types/countItemDto";
import { MeasureItemDto, MeasuresItemizedCompactDto, MeasuresSummarizedDto } from "src/types/dashboardDatasetDto";
import { MeasuresItemizedDto } from "src/types/locationChartDto";

import { CSV_DELIMITER } from "./config.helper";
import { getGranularityFromInterval, getSummarizedGranularityFromInterval } from "./date.helper";

/* placeholder for empty cells in csv download */
const NO_DATA_PLACEHOLDER = "";

/*
 * Converts UTC date or local date input into ISO-8601 date string that includes the user's local timezone offset.
 *
 * Use Cases:
 * 1) UTC Date Input
 * Example:
 *   const utcDate = new Date("2026-03-01T23:00:00Z");
 *
 * 2) Non-UTC Date String (interpreted as local time)
 * Example:
 *   const gmtDateStr = new Date("2026-03-02T00:00:00");
 *
 * Both examples should produce the same result when the user's local timezone is +01:00:
 *   2026-03-02T00:00:00+01:00
 *
 * Behavior:
 * - Automatically handles Daylight Saving Time (DST)
 * - Uses browser/system timezone settings
 * - Produces format: YYYY-MM-DDTHH:mm:ss+HH:MM
 */
export function getLocalISOString(inputDate: string | Date) {
  const date = new Date(inputDate);
  const pad = (num: number) => String(num).padStart(2, "0");

  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());

  const offset = -date.getTimezoneOffset(); // minutes
  const sign = offset >= 0 ? "+" : "-";
  const offsetHours = pad(Math.floor(Math.abs(offset) / 60));
  const offsetMinutes = pad(Math.abs(offset) % 60);

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${sign}${offsetHours}:${offsetMinutes}`;
}

export function getExportFilename(type: ComponentId, slug: string): string {
  const date = new Date().toISOString().slice(0, 10);

  switch (type) {
    case ComponentId.FootfallEvolution:
      return `site_${slug}_count_${date}.csv`;

    case ComponentId.FootfallPerEntrance:
      return `site_${slug}_count-per-entrance_${date}.csv`;

    case ComponentId.WeatherEventsInfluence:
      return `site_${slug}_weather-event-influence_${date}.csv`;

    case ComponentId.OccupancyLastHour:
      return `site_${slug}_occupancy-last-hour_${date}.csv`;

    case ComponentId.Footfall:
      return `region_${slug}_count_${date}.csv`;

    case ComponentId.FootfallPinnedLocations:
      return `region_${slug}_count-pinned_${date}.csv`;

    default:
      return `export_${slug}_${date}.csv`;
  }
}

const toSlug = (name: string) =>
  name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

export function buildCSV(headers: string[], rows: Record<string, unknown>[], delimeter: string): string {
  const escape = (value: unknown) => {
    if (value === null || value === undefined) return "";
    const str = String(value).replace(/"/g, '""');
    return /[",;\n]/.test(str) ? `"${str}"` : str;
  };

  const csvRows = rows.map((row) => headers.map((h) => escape(row[h])).join(delimeter));

  return [headers.join(delimeter), ...csvRows].join("\n");
}

export function triggerDownload(csv: string, filename: string) {
  const BOM = "\uFEFF";
  const blob = new Blob([BOM + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export function downloadCSVFromRows(rows: Record<string, unknown>[], componentId: ComponentId, locationName: string) {
  if (!rows.length) {
    return;
  }

  const headers = Object.keys(rows[0]);
  const slug = toSlug(locationName);
  const filename = getExportFilename(componentId, slug);
  const csv = buildCSV(headers, rows, CSV_DELIMITER);

  triggerDownload(csv, filename);
}

export function mapSiteFootfallEvolution(
  dto: MeasuresItemizedDto,
  locationName: string,
  intervalType: IntervalType
): Record<string, unknown>[] {
  const granularity = getGranularityFromInterval(intervalType);

  return dto?.data?.map((item) => ({
    locationName,
    measureTime: getLocalISOString(item.currentDate),
    comparisonMeasureTime: getLocalISOString(item.pastDate),
    count: item.currentValue ?? NO_DATA_PLACEHOLDER,
    comparisonCount: item.pastValue ?? NO_DATA_PLACEHOLDER,
    granularity: granularity ?? NO_DATA_PLACEHOLDER,
  }));
}

export function mapSiteWeatherEventsInfluence(dto: MeasuresItemizedDto, locationName: string): Record<string, unknown>[] {
  return dto?.data?.map((item) => ({
    locationName,
    measureTime: getLocalISOString(item.currentDate),
    comparisonMeasureTime: getLocalISOString(item.pastDate),
    count: item.currentValue ?? NO_DATA_PLACEHOLDER,
    comparisonCount: item.pastValue ?? NO_DATA_PLACEHOLDER,
    weatherCondition: item.status ?? NO_DATA_PLACEHOLDER,
    temperature: item.temperature ?? NO_DATA_PLACEHOLDER,
    comparisonWeatherCondition: item.pastStatus ?? NO_DATA_PLACEHOLDER,
    comparisonTemperature: item.pastTemperature ?? NO_DATA_PLACEHOLDER,
    eventName: item.event ?? NO_DATA_PLACEHOLDER,
    eventDescription: item.eventDescription ?? NO_DATA_PLACEHOLDER,
    comparisonEventName: item.pastEvent ?? NO_DATA_PLACEHOLDER,
    comparisonEventDescription: item.pastEventDescription ?? NO_DATA_PLACEHOLDER,
    granularity: Granularity.Day,
  }));
}

export function mapSiteOccupancyLastHour(dto: CountItemsDTO, locationName: string): Record<string, unknown>[] {
  if (!dto?.value) return [];

  const sumByTime: Record<string, { inCount: number; outCount: number }> = {};

  for (const item of dto.value) {
    const time = item.MeasureTime.valueOf();
    const inCount = item.InCount ?? 0;
    const outCount = item.OutCount ?? 0;

    if (!sumByTime[time]) {
      sumByTime[time] = { inCount, outCount };
    } else {
      sumByTime[time].inCount += inCount;
      sumByTime[time].outCount += outCount;
    }
  }

  return Object.entries(sumByTime)
    .sort(([t1], [t2]) => new Date(t1).getTime() - new Date(t2).getTime())
    .map(([time, counts]) => ({
      locationName,
      measureTime: getLocalISOString(time),
      inCount: counts.inCount,
      outCount: counts.outCount,
      total: counts.inCount - counts.outCount,
      granularity: dto.value[0]?.Granularity ?? null,
    }));
}

export function mapRegionFootfall(dto: MeasuresSummarizedDto, intervalType: IntervalType): Record<string, unknown>[] {
  const granularity = getSummarizedGranularityFromInterval(intervalType);

  return dto?.data?.map((item) => ({
    locationName: item.localName,
    measureTime: getLocalISOString(dto.currentDateRange.start),
    comparisonMeasureTime: getLocalISOString(dto.pastDateRange.start),
    count: item.currentValue ?? NO_DATA_PLACEHOLDER,
    comparisonCount: item.pastValue ?? NO_DATA_PLACEHOLDER,
    granularity: granularity,
  }));
}

export function mapFootfallPerEntrance(
  dto: MeasuresSummarizedDto,
  intervalType: IntervalType,
  locationName: string
): Record<string, unknown>[] {
  const granularity = getSummarizedGranularityFromInterval(intervalType);

  return dto?.data?.map((item) => ({
    locationName,
    entranceName: item.localName,
    measureTime: getLocalISOString(dto.currentDateRange.start),
    comparisonMeasureTime: getLocalISOString(dto.pastDateRange.start),
    count: item.currentValue ?? NO_DATA_PLACEHOLDER,
    comparisonCount: item.pastValue ?? NO_DATA_PLACEHOLDER,
    granularity: granularity,
  }));
}

export function mapRegionFootfallPinnedSitesCompact(
  dto: MeasuresItemizedCompactDto,
  intervalType: IntervalType,
  locationName: string
): Record<string, unknown>[] {
  const granularity = getGranularityFromInterval(intervalType);

  return dto?.data?.flatMap((item) => {
    const current = (item.currentValues ?? []) as MeasureItemDto[];
    const past = (item.pastValues ?? []) as MeasureItemDto[];

    return current.map((cv, index) => {
      const pv = past[index] ?? null;

      return {
        locationName,
        siteName: item.locationName ?? NO_DATA_PLACEHOLDER,
        measureTime: getLocalISOString(cv.measureTime),
        comparisonMeasureTime: pv?.measureTime ? getLocalISOString(pv?.measureTime) : NO_DATA_PLACEHOLDER,
        count: cv.count,
        comparisonCount: pv?.count ?? NO_DATA_PLACEHOLDER,
        granularity: granularity ?? NO_DATA_PLACEHOLDER,
      };
    });
  });
}
